import React, { useState, useEffect, useRef } from "react";
import "./ProfileUploadModal.css";
import { apiFetch } from "../utils/apifetch";

/* ================= CONFIG ================= */
const MAX_WRITEUP_PDF_SIZE_MB = 2;
const MAX_WRITEUP_PDF_SIZE_BYTES =
  MAX_WRITEUP_PDF_SIZE_MB * 1024 * 1024;

/* ================= HELPERS ================= */

const buildFileUrl = (path) => {
  if (!path) return null;
  const normalized = path.replace(/\\/g, "/").replace(/^\/+/, "");
  const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, "");
  return `${baseUrl}/${normalized}`;
};

const extractFilename = (path) => {
  if (!path) return "";
  return path.replace(/\\/g, "/").split("/").pop();
};

export default function ProfileUploadModal({
  mode = "add", // "add" | "edit"
  profileData = null,
  onClose,
  onProfileUploaded, // expects status: "updated" | "nochange"
}) {
  const isEditMode = mode === "edit";

  /* ================= ORIGINAL SNAPSHOT ================= */
  const originalRef = useRef({
    ProfileName: "",
    ProfileTitle: "",
  });
  const originalPhotoRef = useRef(null);
  const originalPdfRef = useRef(null);

  /* ================= FORM STATE ================= */
  const [ProfileName, setProfileName] = useState("");
  const [ProfileTitle, setProfileTitle] = useState("");

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= PREFILL (EDIT MODE) ================= */
  useEffect(() => {
    if (isEditMode && profileData) {
      setProfileName(profileData.ProfileName || "");
      setProfileTitle(profileData.ProfileTitle || "");

      originalRef.current = {
        ProfileName: profileData.ProfileName || "",
        ProfileTitle: profileData.ProfileTitle || "",
      };

      const photoUrl = buildFileUrl(profileData.PhotoPath);
      const pdfUrl = buildFileUrl(profileData.WriteupPdfPath);

      originalPhotoRef.current = photoUrl;
      originalPdfRef.current = pdfUrl;

      setPhotoPreview(photoUrl);
      setPdfPreviewUrl(pdfUrl);
    }
  }, [isEditMode, profileData]);

  /* ================= CLEANUP ================= */
  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
      if (pdfPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [photoPreview, pdfPreviewUrl]);

  /* ================= CANCEL ================= */
  const handleCancel = () => {
    setPhotoFile(null);
    setPdfFile(null);
    setPhotoPreview(originalPhotoRef.current);
    setPdfPreviewUrl(originalPdfRef.current);
    setError("");
    onClose();
  };

  /* ================= CHANGE DETECTION ================= */
  const isProfileChanged = () => {
    if (!isEditMode) return true;

    if (ProfileName !== originalRef.current.ProfileName) return true;
    if (ProfileTitle !== originalRef.current.ProfileTitle) return true;
    if (photoFile) return true;
    if (pdfFile) return true;

    return false;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!ProfileName.trim()) {
      setError("Profile name is required.");
      return;
    }

    /* ✅ NO-CHANGE FLOW (SAME AS SUCCESS UX) */
    if (isEditMode && !isProfileChanged()) {
      onProfileUploaded?.("nochange");
      onClose();
      return;
    }

    if (!isEditMode && (!photoFile || !pdfFile)) {
      setError("Photo and write-up PDF are required.");
      return;
    }

    const formData = new FormData();
    formData.append("ProfileName", ProfileName.trim());
    formData.append("ProfileTitle", ProfileTitle.trim());

    if (photoFile) formData.append("photo", photoFile);
    if (pdfFile) formData.append("writeupPdf", pdfFile);

    try {
      setLoading(true);
      setError("");

      const endpoint = isEditMode
        ? `/profile/${profileData.Id}`
        : "/profile";

      const method = isEditMode ? "PUT" : "POST";

      await apiFetch(endpoint, { method, body: formData });

      onProfileUploaded?.("updated");
      onClose();
    } catch (err) {
      console.error(err);
      setError("Failed to save profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <button className="close-btn" onClick={handleCancel}>
          ✕
        </button>

        <h2>{isEditMode ? "Edit Profile" : "Upload Profile"}</h2>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* PROFILE NAME */}
          <div className="form-group">
            <label>Profile Name</label>
            <input
              type="text"
              value={ProfileName}
              onChange={(e) => setProfileName(e.target.value)}
              required
            />
          </div>

          {/* PROFILE TITLE */}
          <div className="form-group">
            <label>Profile Title</label>
            <input
              type="text"
              maxLength={160}
              value={ProfileTitle}
              onChange={(e) => setProfileTitle(e.target.value)}
            />
          </div>

          {/* PHOTO */}
          <div className="form-group">
            <label>Profile Photo</label>

            {photoPreview && (
              <>
                <img
                  src={photoPreview}
                  alt="Photo Preview"
                  className="photo-preview"
                />
                <div className="existing-file">
                  Current file:{" "}
                  <strong>
                    {profileData?.ImageFilename ||
                      extractFilename(profileData?.PhotoPath)}
                  </strong>
                </div>
              </>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                setPhotoFile(file);
                setPhotoPreview(URL.createObjectURL(file));
              }}
            />
          </div>

          {/* PDF */}
          <div className="form-group">
            <label>Write-up PDF</label>

            {pdfPreviewUrl && (
              <div className="pdf-preview">
                <span>
                  Current file:{" "}
                  <strong>
                    {profileData?.WriteupPdfOriginalName ||
                      extractFilename(profileData?.WriteupPdfPath)}
                  </strong>
                </span>
                <button
                  type="button"
                  onClick={() => window.open(pdfPreviewUrl, "_blank")}
                >
                  Preview
                </button>
              </div>
            )}

            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                if (file.size > MAX_WRITEUP_PDF_SIZE_BYTES) {
                  setError(
                    `PDF must be ≤ ${MAX_WRITEUP_PDF_SIZE_MB} MB`
                  );
                  return;
                }

                setPdfFile(file);
                setPdfPreviewUrl(URL.createObjectURL(file));
              }}
            />
          </div>

          {/* ACTIONS */}
          <div className="modal-actions">
            <button type="submit" disabled={loading}>
              {isEditMode ? "Update Profile" : "Upload Profile"}
            </button>

            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
